/** Custom tag-category-schema editor.
 *
 * Two modes: a MUI "Form" builder and a raw "JSON" escape hatch (the existing
 * JsonEditorWidget in code mode). The form is the two-column `SchemaBoard`:
 * property groups on the left, Ungrouped on the right, properties dragged
 * within and between them, each opening its typed editor with our `x-`
 * vocabulary (CP-7988). It's a controlled component — every edit calls
 * `onChange(next)` with a new schema object; the consumer owns save + concurrency.
 */
import CodeIcon from '@mui/icons-material/Code';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Alert, Box, Button, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useRef, useState } from 'react';

import { JsonEditorWidget, type JsonEditorHandle } from '@/components/JsonEditor/JsonEditorWidget';

import { SchemaBoard } from './SchemaBoard';
import { emptySchema } from './schemaEditorOps';
import type { TagSchema } from './vocabulary';

interface Props {
  value: TagSchema | null | undefined;
  onChange: (next: TagSchema) => void;
  readOnly?: boolean;
}

export function SchemaEditor({ value, onChange, readOnly = false }: Props) {
  const schema = value ?? emptySchema();
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const jsonRef = useRef<JsonEditorHandle>(null);

  const applyJson = (): boolean => {
    try {
      const next = jsonRef.current?.getValue();
      if (next && typeof next === 'object' && !Array.isArray(next)) {
        onChange(next as TagSchema);
        setJsonError(null);
        return true;
      }
      setJsonError('The schema must be a JSON object.');
      return false;
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'Invalid JSON.');
      return false;
    }
  };

  const switchMode = (next: 'form' | 'json' | null) => {
    if (!next || next === mode) return;
    // Leaving JSON → fold edits back into the form model first.
    if (mode === 'json' && !readOnly && !applyJson()) return;
    setMode(next);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small" exclusive value={mode}
          onChange={(_, v) => switchMode(v as 'form' | 'json' | null)}
        >
          <ToggleButton value="form"><ViewListIcon fontSize="small" sx={{ mr: 0.5 }} /> Form</ToggleButton>
          <ToggleButton value="json"><CodeIcon fontSize="small" sx={{ mr: 0.5 }} /> JSON</ToggleButton>
        </ToggleButtonGroup>
        {mode === 'json' && !readOnly && (
          <Button size="small" variant="outlined" onClick={applyJson}>Apply JSON</Button>
        )}
      </Stack>

      {jsonError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJsonError(null)}>{jsonError}</Alert>}

      {mode === 'json' ? (
        <Box sx={{ height: 460, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          {/* Remount with the current value each time JSON mode is entered. */}
          <JsonEditorWidget key={mode} ref={jsonRef} initialValue={schema} readOnly={readOnly} mode="code" />
        </Box>
      ) : (
        <SchemaBoard schema={schema} readOnly={readOnly} onChange={onChange} />
      )}
    </Box>
  );
}
